import React, { useState, useEffect } from "react";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  Users,
  Briefcase,
  ChartLine,
  TrendUp,
  Gift,
  CheckCircle,
  CalendarDots,
  Clock,
  ListChecks,
  MagnifyingGlass,
  Lightning,
  Target,
  GearSix,
  Play,
  Coffee,
  Eye,
  EyeSlash,
  DotsSixVertical
} from "@phosphor-icons/react";
import { DashboardWidget, AttendanceRecord } from "@/types";
import { AutoCheckoutMetrics } from "@/components/dashboard/AutoCheckoutMetrics";

export const DashboardView = () => {
  const {
    currentUser,
    dashboardLayout,
    setDashboardLayout,
    teamMembers,
    attendanceRecords,
    setAttendanceRecords,
    leaveRequests,
    timesheetEntries,
    weeklyGoals,
    attendanceRate,
    streak,
    setStreak,
    totalLeaveBalance,
    usedLeaveBalance,
    isCheckedIn,
    setIsCheckedIn,
    isOnBreak,
    setIsOnBreak,
    breakStartTime,
    setBreakStartTime,
    totalBreakTime,
    setTotalBreakTime,
    geofenceState,
    setActiveSection,
    isAuthenticated
  } = useApp();

  const [currentDate, setCurrentDate] = useState("");
  const [currentTime, setCurrentTime] = useState("");
  const [isCustomizingDashboard, setIsCustomizingDashboard] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Time update effect
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setCurrentDate(
        now.toLocaleDateString("en-US", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        })
      );
      setCurrentTime(
        now.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
        })
      );
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  // Derived State
  const userProfile = currentUser
    ? {
        name: currentUser.name,
        role: currentUser.role.charAt(0).toUpperCase() + currentUser.role.slice(1),
      }
    : { name: "User", role: "Employee" };

  const totalHoursThisWeek = (timesheetEntries || [])
    .filter((entry) => {
      const entryDate = new Date(entry.date);
      const weekStart = new Date();
      weekStart.setDate(weekStart.getDate() - weekStart.getDay());
      return entryDate >= weekStart;
    })
    .reduce((sum, entry) => sum + entry.hours, 0);

  const currentWeekGoals = (weeklyGoals || []).filter((goal) => {
    const goalWeek = new Date(goal.week);
    const currentWeek = new Date();
    currentWeek.setDate(currentWeek.getDate() - currentWeek.getDay());
    return goalWeek >= currentWeek;
  });

  // Handlers
  const handleToggleCustomizeDashboard = () => {
    setIsCustomizingDashboard(!isCustomizingDashboard);
  };

  const getDefaultDashboardLayout = () => ({
    widgets: [
      { id: "total-employees", type: "total-employees", title: "Total Employees", visible: true, size: "medium", order: 0 },
      { id: "open-roles", type: "open-roles", title: "Open Roles", visible: true, size: "medium", order: 1 },
      { id: "attendance-rate", type: "attendance-rate", title: "Attendance Rate", visible: true, size: "medium", order: 2 },
      { id: "performance-trends", type: "performance-trends", title: "Performance Trends", visible: true, size: "large", order: 3 },
      { id: "workforce-diversity", type: "workforce-diversity", title: "Workforce Diversity", visible: true, size: "large", order: 4 },
      { id: "celebrations", type: "celebrations", title: "Celebrations", visible: true, size: "large", order: 5 },
      { id: "recent-activities", type: "recent-activities", title: "Recent Activities", visible: true, size: "large", order: 6 },
      { id: "pending-tasks", type: "pending-tasks", title: "Pending Tasks", visible: true, size: "large", order: 7 },
    ],
    lastModified: new Date().toISOString(),
  });

  const handleResetDashboardLayout = () => {
    // We need to cast the type or ensure getDefaultDashboardLayout returns correct type
    // Since we can't import the one from AppContext easily without exporting it, I redefined it here.
    // Ideally it should be shared.
    setDashboardLayout(getDefaultDashboardLayout() as any); 
    setIsCustomizingDashboard(false);
    toast.success("Dashboard layout reset to default");
  };

  const handleToggleWidgetVisibility = (id: string) => {
    const updatedWidgets = dashboardLayout.widgets.map((w) =>
      w.id === id ? { ...w, visible: !w.visible } : w
    );
    setDashboardLayout({
      ...dashboardLayout,
      widgets: updatedWidgets,
      lastModified: new Date().toISOString(),
    });
  };

  const handleChangeWidgetSize = (id: string, size: "small" | "medium" | "large") => {
    const updatedWidgets = dashboardLayout.widgets.map((w) =>
      w.id === id ? { ...w, size } : w
    );
    setDashboardLayout({
      ...dashboardLayout,
      widgets: updatedWidgets,
      lastModified: new Date().toISOString(),
    });
  };

  const handleCheckIn = async () => {
    setIsLoading(true);

    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      const currentTimeStr = new Date().toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });

      if (isCheckedIn) {
        // Check out
        await api.checkOut(today);
        const updatedRecords = (attendanceRecords || []).map((record) => {
          if (record.date === today && !record.checkOut) {
            return { ...record, checkOut: currentTimeStr };
          }
          return record;
        });
        setAttendanceRecords(updatedRecords);
        setIsCheckedIn(false);
        setTotalBreakTime(0); // Reset break time for next day
        toast.success("🏁 Checked out successfully! Have a great day!");
      } else {
        // Check in
        const location =
            geofenceState.isEnabled && geofenceState.currentZoneId
              ? "Geofence Zone"
              : "Office - Main Building";

        await api.checkIn(location, today);

        const newRecord: AttendanceRecord = {
          id: `att-${Date.now()}`,
          date: today,
          checkIn: currentTimeStr,
          status: "present",
          location,
          breakTime: 0,
        };

        setAttendanceRecords([newRecord, ...(attendanceRecords || [])]);
        setIsCheckedIn(true);
        setStreak((current) => current + 1);
        // completeOnboardingStep("first-checkin"); // Skipped as not in Context
        toast.success(
          "🎉 Checked in successfully! Ready to make today amazing!",
        );
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to update attendance");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBreakToggle = () => {
    if (isOnBreak) {
      // End break
      if (breakStartTime) {
        const breakDuration = Math.floor(
          (Date.now() - new Date(breakStartTime).getTime()) / (1000 * 60),
        );
        setTotalBreakTime((current) => current + breakDuration);
        setBreakStartTime(null);
        toast.success(
          `🏃‍♂️ Break ended! You took a ${breakDuration} minute break.`,
        );
      }
      setIsOnBreak(false);
    } else {
      // Start break
      setBreakStartTime(new Date().toISOString());
      setIsOnBreak(true);
      toast.success("☕ Break started! Take your time.");
    }
  };

  if (!isAuthenticated) return null;

  return (
    <div className="space-y-6">
      {/* Quick Actions & Status */}
      <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h2 className="text-xl sm:text-2xl font-bold truncate">
                Welcome back, {userProfile.name.substring(0, 25)}! 👋
              </h2>
              <p className="text-muted-foreground mt-1 text-sm sm:text-base whitespace-nowrap overflow-hidden text-ellipsis">
                {currentDate} • {currentTime}
              </p>
              {streak > 0 && (
                <div className="flex items-center gap-2 mt-2 text-sm text-amber-600 font-medium flex-shrink-0 line-clamp-1">
                  <Lightning className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{streak} day streak! 🔥</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
              {/* Dashboard Customization Button */}
              <Button
                variant="outline"
                size="lg"
                onClick={handleToggleCustomizeDashboard}
                className="flex-1 sm:flex-initial transition-all duration-300 hover:scale-105 touch-manipulation min-h-[48px]"
                data-testid="customize-dashboard-btn"
              >
                <GearSix className="w-5 h-5 sm:mr-2" />
                <span className="hidden sm:inline">
                  {isCustomizingDashboard ? "Done" : "Customize"}
                </span>
              </Button>
              {isCheckedIn && (
                <Button
                  variant={isOnBreak ? "default" : "outline"}
                  size="lg"
                  onClick={handleBreakToggle}
                  className="flex-1 sm:flex-initial transition-all duration-300 hover:scale-105 touch-manipulation min-h-[48px]"
                >
                  {isOnBreak ? (
                    <>
                      <Play className="w-4 h-4 sm:mr-2" />
                      <span className="hidden sm:inline">Resume</span>
                    </>
                  ) : (
                    <>
                      <Coffee className="w-4 h-4 sm:mr-2" />
                      <span className="hidden sm:inline">Break</span>
                    </>
                  )}
                </Button>
              )}
              <Button
                size="lg"
                onClick={handleCheckIn}
                disabled={isLoading}
                className={`flex-1 sm:flex-initial transition-all duration-300 hover:scale-105 touch-manipulation min-h-[48px] ${
                  isCheckedIn
                    ? "bg-[var(--destructive)] hover:bg-[var(--destructive)] opacity-75 hover:opacity-90"
                    : "bg-[var(--success)] hover:bg-[var(--success)] opacity-90 hover:opacity-100"
                }`}
              >
                {isCheckedIn ? (
                  <>
                    <Clock className="w-5 h-5 sm:mr-2" />
                    <span className="hidden sm:inline">Check Out</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5 sm:mr-2" />
                    <span className="hidden sm:inline">Check In</span>
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dashboard Customization Panel */}
      {isCustomizingDashboard && (
        <Card className="bg-muted/50 border-primary/20">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex-1">
                <h3 className="font-semibold text-primary">
                  Customization Mode Active
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Drag widgets to reorder • Click eye icon to show/hide
                  • Click size buttons to resize
                </p>
              </div>
              <Button
                variant="outline"
                onClick={handleResetDashboardLayout}
                className="w-full sm:w-auto"
                data-testid="reset-dashboard-btn"
              >
                Reset to Default
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Widget List for Customization */}
      {isCustomizingDashboard && (
        <Card>
          <CardHeader>
            <CardTitle>Widget Controls</CardTitle>
            <CardDescription>
              Show, hide, or resize dashboard widgets
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {dashboardLayout.widgets
                .sort((a, b) => a.order - b.order)
                .map((widget) => (
                  <div
                    key={widget.id}
                    className="flex items-center justify-between p-3 border rounded-lg bg-background"
                    data-testid={`widget-control-${widget.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <DotsSixVertical className="w-5 h-5 text-muted-foreground cursor-grab" />
                      <div>
                        <div className="font-medium">
                          {widget.title}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Size: {widget.size}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Size buttons */}
                      <div className="flex gap-1">
                        {(["small", "medium", "large"] as const).map(
                          (size) => (
                            <Button
                              key={size}
                              variant={
                                widget.size === size
                                  ? "default"
                                  : "outline"
                              }
                              size="sm"
                              onClick={() =>
                                handleChangeWidgetSize(widget.id, size)
                              }
                              className="px-2 text-xs"
                              data-testid={`size-${size}-${widget.id}`}
                            >
                              {size[0].toUpperCase()}
                            </Button>
                          ),
                        )}
                      </div>
                      {/* Visibility toggle */}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          handleToggleWidgetVisibility(widget.id)
                        }
                        data-testid={`visibility-${widget.id}`}
                      >
                        {widget.visible ? (
                          <Eye className="w-4 h-4" />
                        ) : (
                          <EyeSlash className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top Metrics - Responsive Layout */}
      {dashboardLayout.widgets.find((w) => w.type === "total-employees")
        ?.visible && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {/* Total Employees Card */}
          {dashboardLayout.widgets.find(
            (w) => w.type === "total-employees",
          )?.visible && (
            <Card
              className="hover:shadow-lg transition-shadow"
              data-widget-id="total-employees"
            >
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Total Employees
                  </CardTitle>
                  <Users className="w-5 h-5 text-blue-500" />
                </div>
              </CardHeader>
              <CardContent>
                {/* Skeleton Loader Example as requested */}
                {!teamMembers ? (
                   <div className="space-y-2">
                     <Skeleton className="h-8 w-20" />
                     <Skeleton className="h-4 w-32" />
                   </div>
                ) : (
                <div className="flex items-end justify-between">
                  <div>
                    <div className="text-3xl font-bold">
                      {(teamMembers || []).length}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Active team members
                    </p>
                  </div>
                  <div className="w-16 h-16">
                    <svg
                      viewBox="0 0 100 100"
                      className="w-full h-full"
                    >
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        fill="none"
                        stroke="#e2e8f0"
                        strokeWidth="8"
                      />
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        fill="none"
                        stroke="#3b82f6"
                        strokeWidth="8"
                        strokeDasharray={`${((teamMembers || []).length / 1250) * 251} 251`}
                        strokeLinecap="round"
                        transform="rotate(-90 50 50)"
                      />
                    </svg>
                  </div>
                </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Open Roles Card */}
          {dashboardLayout.widgets.find((w) => w.type === "open-roles")
            ?.visible && (
            <Card
              className="hover:shadow-lg transition-shadow"
              data-widget-id="open-roles"
            >
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Open Roles
                  </CardTitle>
                  <Briefcase className="w-5 h-5 text-amber-500" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-end justify-between">
                  <div>
                    <div className="text-3xl font-bold">0</div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Positions to fill
                    </p>
                    <div className="flex items-center gap-1 mt-2 text-xs text-red-600">
                      <span>Urgent: 0</span>
                      <TrendUp className="w-3 h-3" />
                    </div>
                  </div>
                  <div className="space-y-1">
                    {[20, 35, 25, 40].map((height, i) => (
                      <div key={i} className="flex gap-1">
                        <div
                          className="w-2 bg-slate-200 rounded"
                          style={{ height: `${height}px` }}
                        ></div>
                        <div
                          className="w-2 bg-amber-400 rounded"
                          style={{ height: `${height * 0.7}px` }}
                        ></div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Turnover Rate Card */}
          {dashboardLayout.widgets.find(
            (w) => w.type === "attendance-rate",
          )?.visible && (
            <Card
              className="hover:shadow-lg transition-shadow"
              data-widget-id="attendance-rate"
            >
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Attendance Rate
                  </CardTitle>
                  <ChartLine className="w-5 h-5 text-emerald-500" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-end justify-between">
                  <div>
                    <div className="text-3xl font-bold">
                      {attendanceRate}%
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      This month
                    </p>
                    <div className="flex items-center gap-1 mt-2 text-xs text-emerald-600">
                      <TrendUp className="w-3 h-3" />
                      <span>+2.1% from last month</span>
                    </div>
                  </div>
                  <div className="w-20 h-12">
                    <svg
                      viewBox="0 0 100 50"
                      className="w-full h-full"
                      preserveAspectRatio="none"
                    >
                      <polyline
                        points="0,40 20,35 40,30 60,25 80,20 100,15"
                        fill="none"
                        stroke="#10b981"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <polyline
                        points="0,40 20,35 40,30 60,25 80,20 100,15 100,50 0,50"
                        fill="url(#gradient)"
                        opacity="0.3"
                      />
                      <defs>
                        <linearGradient
                          id="gradient"
                          x1="0%"
                          y1="0%"
                          x2="0%"
                          y2="100%"
                        >
                          <stop offset="0%" stopColor="#10b981" />
                          <stop
                            offset="100%"
                            stopColor="#10b981"
                            stopOpacity="0"
                          />
                        </linearGradient>
                      </defs>
                    </svg>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Auto-Checkout Metrics */}
      <AutoCheckoutMetrics />

      {/* Charts Row */}
      {(dashboardLayout.widgets.find(
        (w) => w.type === "performance-trends",
      )?.visible ||
        dashboardLayout.widgets.find(
          (w) => w.type === "workforce-diversity",
        )?.visible) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Performance Trends Chart */}
          {dashboardLayout.widgets.find(
            (w) => w.type === "performance-trends",
          )?.visible && (
            <Card
              className="col-span-1 lg:col-span-1"
              data-widget-id="performance-trends"
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Employee Performance Trends</CardTitle>
                    <CardDescription>
                      Quarterly performance metrics
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <Badge variant="outline" className="bg-primary/10">
                      Q1
                    </Badge>
                    <Badge variant="outline" className="bg-primary/10">
                      Q2
                    </Badge>
                    <Badge variant="outline" className="bg-primary/10">
                      Q3
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <ChartLine className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Performance chart visualization</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Workforce Diversity */}
          {dashboardLayout.widgets.find(
            (w) => w.type === "workforce-diversity",
          )?.visible && (
            <Card data-widget-id="workforce-diversity">
              <CardHeader>
                <CardTitle>Workforce Diversity</CardTitle>
                <CardDescription>
                  Team composition breakdown
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64 flex items-center justify-center">
                  <div className="relative w-48 h-48">
                    <svg
                      viewBox="0 0 100 100"
                      className="w-full h-full transform -rotate-90"
                    >
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        fill="none"
                        stroke="#e2e8f0"
                        strokeWidth="20"
                      />
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        fill="none"
                        stroke="#3b82f6"
                        strokeWidth="20"
                        strokeDasharray="75 251"
                        strokeLinecap="round"
                      />
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        fill="none"
                        stroke="#8b5cf6"
                        strokeWidth="20"
                        strokeDasharray="63 251"
                        strokeDashoffset="-75"
                        strokeLinecap="round"
                      />
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        fill="none"
                        stroke="#14b8a6"
                        strokeWidth="20"
                        strokeDasharray="50 251"
                        strokeDashoffset="-138"
                        strokeLinecap="round"
                      />
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        fill="none"
                        stroke="#f59e0b"
                        strokeWidth="20"
                        strokeDasharray="63 251"
                        strokeDashoffset="-188"
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-2xl font-bold">
                          {(teamMembers || []).length}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Total
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                  {[
                    {
                      label: "Gender",
                      color: "bg-blue-500",
                      value: "30%",
                    },
                    {
                      label: "Ethnicity",
                      color: "bg-purple-500",
                      value: "25%",
                    },
                    {
                      label: "Age",
                      color: "bg-teal-500",
                      value: "20%",
                    },
                    {
                      label: "Other",
                      color: "bg-amber-500",
                      value: "25%",
                    },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="flex items-center gap-2"
                    >
                      <div
                        className={`w-3 h-3 rounded-full ${item.color}`}
                      ></div>
                      <span className="text-xs text-muted-foreground">
                        {item.label}
                      </span>
                      <span className="text-xs font-medium ml-auto">
                        {item.value}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Birthdays & Anniversaries */}
      {dashboardLayout.widgets.find((w) => w.type === "celebrations")
        ?.visible && (
        <Card data-widget-id="celebrations">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Gift className="w-5 h-5" />
                  Celebrations 🎉
                </CardTitle>
                <CardDescription>
                  Upcoming birthdays and work anniversaries
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {(() => {
              const today = new Date();
              const celebrations: Array<{
                name: string;
                type: "birthday" | "anniversary";
                date: Date;
                years?: number;
                avatar: string;
                department: string;
              }> = [];

              // Get celebrations from teamMembers
              (teamMembers || []).forEach((member) => {
                // Mock birthday data (in production, would come from employee profiles)
                const randomDay = Math.floor(Math.random() * 28) + 1;
                const randomMonth = Math.floor(Math.random() * 12);
                const birthDate = new Date(
                  1990,
                  randomMonth,
                  randomDay,
                );
                const nextBirthday = new Date(
                  today.getFullYear(),
                  birthDate.getMonth(),
                  birthDate.getDate(),
                );

                if (nextBirthday < today) {
                  nextBirthday.setFullYear(today.getFullYear() + 1);
                }

                const daysUntilBirthday = Math.floor(
                  (nextBirthday.getTime() - today.getTime()) /
                    (1000 * 60 * 60 * 24),
                );

                if (daysUntilBirthday <= 30) {
                  celebrations.push({
                    name: member.name,
                    type: "birthday",
                    date: nextBirthday,
                    avatar: member.avatar,
                    department: member.department,
                  });
                }

                // Mock work anniversary (would come from hire date)
                const hireYear = 2020 + Math.floor(Math.random() * 5);
                const hireDate = new Date(
                  hireYear,
                  randomMonth,
                  randomDay,
                );
                const nextAnniversary = new Date(
                  today.getFullYear(),
                  hireDate.getMonth(),
                  hireDate.getDate(),
                );

                if (nextAnniversary < today) {
                  nextAnniversary.setFullYear(today.getFullYear() + 1);
                }

                const daysUntilAnniversary = Math.floor(
                  (nextAnniversary.getTime() - today.getTime()) /
                    (1000 * 60 * 60 * 24),
                );
                const yearsOfService =
                  today.getFullYear() -
                  hireYear +
                  (nextAnniversary < today ? 0 : 1);

                if (daysUntilAnniversary <= 30 && yearsOfService >= 1) {
                  celebrations.push({
                    name: member.name,
                    type: "anniversary",
                    date: nextAnniversary,
                    years: yearsOfService,
                    avatar: member.avatar,
                    department: member.department,
                  });
                }
              });

              // Sort by date (nearest first)
              celebrations.sort(
                (a, b) => a.date.getTime() - b.date.getTime(),
              );
              const upcomingCelebrations = celebrations.slice(0, 5);

              return (
                <div>
                  {upcomingCelebrations.length > 0 ? (
                    <div className="space-y-3">
                      {upcomingCelebrations.map(
                        (celebration, index) => {
                          const daysUntil = Math.floor(
                            (celebration.date.getTime() -
                              today.getTime()) /
                              (1000 * 60 * 60 * 24),
                          );
                          const isToday = daysUntil === 0;
                          const dateText = isToday
                            ? "Today!"
                            : daysUntil === 1
                              ? "Tomorrow"
                              : `in ${daysUntil} days`;

                          return (
                            <div
                              key={`${celebration.name}-${celebration.type}-${index}`}
                              className={`flex items-center gap-3 p-3 rounded-lg border ${
                                isToday
                                  ? "bg-yellow-50 border-yellow-200"
                                  : "bg-muted/30"
                              }`}
                            >
                              <div className="text-3xl">
                                {celebration.avatar}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold truncate">
                                  {celebration.name}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  {celebration.type === "birthday" ? (
                                    <>🎂 Birthday</>
                                  ) : (
                                    <>
                                      🎊 {celebration.years}{" "}
                                      {celebration.years === 1
                                        ? "Year"
                                        : "Years"}{" "}
                                      Anniversary
                                    </>
                                  )}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {celebration.department}
                                </div>
                              </div>
                              <div className="text-right">
                                <Badge
                                  variant={
                                    isToday ? "default" : "secondary"
                                  }
                                  className="whitespace-nowrap"
                                >
                                  {dateText}
                                </Badge>
                                {isToday && (
                                  <div className="text-xs text-muted-foreground mt-1">
                                    Say hi! 👋
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        },
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Gift className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>
                        No upcoming celebrations in the next 30 days
                      </p>
                    </div>
                  )}

                  {/* Statistics */}
                  <div className="grid grid-cols-2 gap-3 mt-6 pt-6 border-t">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-pink-600">
                        {
                          celebrations.filter(
                            (c) => c.type === "birthday",
                          ).length
                        }
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Upcoming Birthdays
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-purple-600">
                        {
                          celebrations.filter(
                            (c) => c.type === "anniversary",
                          ).length
                        }
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Work Anniversaries
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Activity & Tasks Row */}
      {(dashboardLayout.widgets.find(
        (w) => w.type === "recent-activities",
      )?.visible ||
        dashboardLayout.widgets.find((w) => w.type === "pending-tasks")
          ?.visible) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Activities */}
          {dashboardLayout.widgets.find(
            (w) => w.type === "recent-activities",
          )?.visible && (
            <Card data-widget-id="recent-activities">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Recent Activities</CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label="View all activities"
                  >
                    <span className="text-xs">View all</span>
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {(attendanceRecords || [])
                    .slice(0, 3)
                    .map((record, index) => (
                      <div
                        key={index}
                        className="flex items-start gap-3"
                      >
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">
                            Check-in recorded
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {record.checkIn} •{" "}
                            {new Date(record.date).toLocaleDateString()}
                          </p>
                        </div>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {record.location || "Office"}
                        </span>
                      </div>
                    ))}
                  {(leaveRequests || [])
                    .slice(0, 2)
                    .map((request, index) => (
                      <div
                        key={`leave-${index}`}
                        className="flex items-start gap-3"
                      >
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <CalendarDots className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">
                            Leave Request
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {request.type} • {request.totalDays} days
                          </p>
                        </div>
                        <Badge
                          variant={
                            request.status === "approved"
                              ? "default"
                              : "secondary"
                          }
                          className="text-xs"
                        >
                          {request.status}
                        </Badge>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick Actions & Navigation */}
          {dashboardLayout.widgets.find(
            (w) => w.type === "pending-tasks",
          )?.visible && (
            <Card data-widget-id="pending-tasks">
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
                <CardDescription>
                  Navigate to common tasks
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    variant="outline"
                    className="h-auto py-4 flex-col gap-2 hover:bg-primary/5 hover:border-primary transition-all"
                    onClick={() => setActiveSection("attendance")}
                  >
                    <Clock className="w-6 h-6 text-primary" />
                    <span className="text-sm font-medium">
                      Attendance
                    </span>
                  </Button>
                  <Button
                    variant="outline"
                    className="h-auto py-4 flex-col gap-2 hover:bg-primary/5 hover:border-primary transition-all"
                    onClick={() => setActiveSection("leaves")}
                  >
                    <CalendarDots className="w-6 h-6 text-primary" />
                    <span className="text-sm font-medium">Leave</span>
                  </Button>
                  <Button
                    variant="outline"
                    className="h-auto py-4 flex-col gap-2 hover:bg-primary/5 hover:border-primary transition-all"
                    onClick={() => setActiveSection("timesheet")}
                  >
                    <ListChecks className="w-6 h-6 text-primary" />
                    <span className="text-sm font-medium">
                      Timesheet
                    </span>
                  </Button>
                  <Button
                    variant="outline"
                    className="h-auto py-4 flex-col gap-2 hover:bg-primary/5 hover:border-primary transition-all"
                    onClick={() => setActiveSection("team")}
                  >
                    <Users className="w-6 h-6 text-primary" />
                    <span className="text-sm font-medium">Team</span>
                  </Button>
                </div>
                <div className="mt-4 pt-4 border-t">
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-2 text-muted-foreground hover:text-foreground"
                    onClick={() => {
                      // Command palette logic - ideally exposed via Context or ref
                      // setCommandOpen(true); 
                    }}
                  >
                    <MagnifyingGlass className="w-4 h-4" />
                    <span className="text-sm">Search anything...</span>
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Quick Stats - Improved Mobile Layout */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Attendance Streak
            </CardTitle>
            <Lightning className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{streak || 0}</div>
            <p className="text-xs text-muted-foreground">
              days in a row
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Hours Logged
            </CardTitle>
            <Clock className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {totalHoursThisWeek.toFixed(1)}h
            </div>
            <p className="text-xs text-muted-foreground">This week</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Leave Balance
            </CardTitle>
            <CalendarDots className="h-4 w-4 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {(totalLeaveBalance || 25) - (usedLeaveBalance || 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              days remaining
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Weekly Goals
            </CardTitle>
            <Target className="h-4 w-4 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {currentWeekGoals.length}
            </div>
            <p className="text-xs text-muted-foreground">
              Active goals
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
